import { EmptyState, ErrorBanner, RefreshBar, SendGateProvider, SkeletonRows, Toaster, TooltipProvider } from '@patchbay/ui'
import { type QueryClient, useIsFetching, useQueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Link, Outlet, redirect, useRouter, useRouterState } from '@tanstack/react-router'
import * as React from 'react'
import { AppPalette } from '../components/app-palette'
import { GlobalKeys } from '../components/global-keys'
import { KeySheet } from '../components/key-sheet'
import { useShellStatus } from '../components/shell-status'
import { forgetSignedInUser, isPublicPath, resolveSession, useSession } from '../features/auth/session'
import { useBrandScope } from '../features/brands/use-brand-scope'
import { isHelpPath } from '../features/help/security'
import { useOfflineCacheUser } from '../features/mobile/pwa'
import { NewCaseHost } from '../features/new-case/host'
import { onUnauthenticated } from '../lib/api/client'
import { setPersistUser } from '../lib/api/persist'
import { sessionQuery } from '../lib/api/queries'
import { useInvalidationStream } from '../lib/sse'
import { UpdateBar } from '../lib/version-check'
import { WorkspaceProvider } from '../lib/workspace'

/**
 * Pages rendered without the staff shell: sign-in, the contractor portal and every /help/* page. Help is checked with
 * isHelpPath (not an exact-match set) so nested guides never mount SignedInShell or redirect an anonymous reader.
 */
function isShellFreePath(pathname: string, href = pathname): boolean {
  return isHelpPath(pathname) || isPublicPath(pathname, href)
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  /**
   * Sign-in redirect (scope §11). Signed out → /signin?return_to=<here>. Unknown (API unreachable) is not
   * signed out: the shell renders read-only from cache instead of bouncing the user to a sign-in page
   * that cannot work either (P11). The API enforces auth on every route regardless.
   */
  beforeLoad: async ({ context, location }) => {
    if (isShellFreePath(location.pathname, location.href)) return
    const session = await resolveSession(context.queryClient)
    if (session.kind === 'signed_out') {
      // Whatever this browser kept belongs to a session that is gone.
      await forgetSignedInUser(context.queryClient, 'unauthorized')
      throw redirect({ to: '/signin', search: { return_to: location.href } })
    }
    if (session.kind === 'signed_in' && session.me.ui_mode === 'simple') {
      const allowed =
        location.pathname === '/my-day' ||
        location.pathname === '/me' ||
        location.pathname === '/notifications' ||
        location.pathname === '/knowledge' ||
        location.pathname.startsWith('/visit-outcome/') ||
        location.pathname.startsWith('/case/') ||
        location.pathname.startsWith('/accounts/') ||
        location.pathname.startsWith('/contacts/')
      if (!allowed) throw redirect({ to: '/my-day' })
    }
  },
  component: Root,
  pendingComponent: () => <SkeletonRows count={6} />,
  errorComponent: ({ error }) => (
    <div className="p-6">
      <ErrorBanner
        title="Something failed to load"
        detail={error instanceof Error ? error.message : String(error)}
        onRetry={() => window.location.reload()}
      />
    </div>
  ),
  notFoundComponent: () => (
    <EmptyState
      title="Nothing here."
      detail="That page does not exist."
      action={
        <Link to="/" className="text-primary">
          Go home
        </Link>
      }
    />
  ),
})

function Root() {
  const location = useRouterState({ select: (s) => s.location })
  const pathname = location.pathname
  React.useEffect(() => {
    if (pathname === '/survey') document.title = 'Ewee Satisfaction'
    else if (isShellFreePath(pathname, location.href)) document.title = 'Ewee'
  }, [location.href, pathname])
  if (isShellFreePath(pathname, location.href)) {
    // Sign-in, the contractor portal and the public help centre: no staff palette, keys, stream or session watch.
    return (
      <TooltipProvider>
        <Outlet />
        <Toaster />
      </TooltipProvider>
    )
  }
  return (
    <WorkspaceProvider>
      <TooltipProvider>
        <SignedInShell />
      </TooltipProvider>
    </WorkspaceProvider>
  )
}

/** Everything that runs once per signed-in tab: SSE, the 401 watch, the send gate, the refresh bar. */
function SignedInShell() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const session = useSession()
  const brandScope = useBrandScope()
  const status = useShellStatus()
  const signedIn = session.kind === 'signed_in'
  const userId = signedIn ? session.me.id : null

  React.useEffect(() => {
    document.title = brandScope.isAll ? 'Patchbay' : (brandScope.selectedBrand?.customer_facing_name ?? 'Patchbay')
  }, [brandScope.isAll, brandScope.selectedBrand?.customer_facing_name])

  // The persisted cache belongs to one person; a different person signing in drops it.
  React.useEffect(() => {
    if (userId) setPersistUser(queryClient, userId)
  }, [queryClient, userId])
  // The service worker keeps offline API reads only in this person's cache (and drops anyone else's).
  useOfflineCacheUser(userId)

  // A 401 anywhere, or the session check now saying "signed out": forget this browser's copy and go to
  // sign-in, coming back here afterwards.
  const toSignIn = React.useCallback(() => {
    if (isShellFreePath(router.state.location.pathname)) return
    const here = router.state.location.href
    void forgetSignedInUser(queryClient, 'unauthorized')
    void router.navigate({ to: '/signin', search: { return_to: here } })
  }, [queryClient, router])
  React.useEffect(() => onUnauthenticated(toSignIn), [toSignIn])
  React.useEffect(() => {
    if (session.kind === 'signed_out') toSignIn()
  }, [session.kind, toSignIn])

  // The API is back after an outage: find out who this is again (the session check failed while it was down).
  const wasReachable = React.useRef(status.api.reachable)
  React.useEffect(() => {
    const cameBack = status.api.reachable && !wasReachable.current
    wasReachable.current = status.api.reachable
    if (cameBack && session.kind === 'unknown') void queryClient.invalidateQueries({ queryKey: sessionQuery().queryKey })
  }, [queryClient, status.api.reachable, session.kind])

  // When the stream closes for good (a 401 closes it too), re-check the session.
  const onClosed = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: sessionQuery().queryKey })
  }, [queryClient])
  useInvalidationStream(queryClient, signedIn, { onClosed })

  // Thin bar while data already on screen revalidates (cold open from cache, SSE invalidations).
  const refreshing = useIsFetching({
    predicate: (q) => q.state.data !== undefined && q.queryKey[0] !== 'liveness' && q.queryKey[0] !== 'session',
  })

  return (
    <SendGateProvider value={status.gate}>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-40">
        <RefreshBar active={refreshing > 0} />
      </div>
      <GlobalKeys />
      <Outlet />
      <AppPalette />
      <NewCaseHost />
      <KeySheet />
      <Toaster />
      <UpdateBar />
    </SendGateProvider>
  )
}
